/**
 * The quote page's parts (docs/features/order-to-invoice/
 * 40_quotes_module_spec.md §10; the journeys branch's round 19 is the visual
 * spec), in the order page's one-card pattern:
 * - the status pill, and Details (the send options: contact, sender, expiry,
 *   reminders, the assigned rep, copies, the note to the customer and the
 *   staff-only internal notes);
 * - the action box: waiting on the customer to decide, a failed quote email
 *   the one thing to do; a closed quote shows its closing line instead;
 * - the chain's panels: Published (the lines as published — quantity options, optional
 *   extras, versions, a held price — with Name versions while published),
 *   Customer decision, Order;
 * - the timeline, for the Activity sheet.
 *
 * Rows are the rail's own (`RailRow`). Every label and sentence the business
 * owns is the view's.
 */
import { Fragment, type ReactNode } from "react";
import { Link } from "react-router";
import type {
  QuoteLineView,
  QuoteStatus,
  QuoteTimelineEntry,
  QuoteView,
} from "@shared-core/data/quote-types";
import {
  acceptedOnBehalfWords,
  contactName,
  lastRemindedAt,
  quoteActionBox,
  quoteLineNo,
  versionWords,
} from "@shared-core/domain/quote-presentation";
import {
  formatDateOrDash,
  formatDateTimeOrDash,
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { formatPriceOrNotYetPriced } from "@shared-core/domain/checkout-delivery";
import {
  deliveryAddressOf,
  isChannelDraft,
  thinPriceOf,
} from "@shared-core/domain/quote-draft-checks";
import { workingDaysLabel } from "@shared-core/domain/supplier-pricing-presentation";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { ActionBoxLayout } from "../../patterns/ActionBoxLayout";
import { RailRow, RailRows } from "../../patterns/LifecycleRail";
import { ObjectChip } from "../../patterns/ObjectChip";
import { DraftChecksSummary, DraftLineChecks } from "./DraftChecks";
import {
  QuoteArtworkRows,
  type QuoteArtworkHandlers,
} from "../artwork/QuoteArtworkRows";
import {
  DetailItem,
  PanelHeading,
  ViewDetail,
  useFocusRef,
} from "../orders/StepPanelParts";

const short = formatShortDateOrDash;
const pounds = formatQuotedPrice;
/** A quantity still waiting for a supplier price has none to show yet. */
const optionPounds = (pence: number | null) =>
  pence === null ? "–" : pounds(pence);
/** A line's price; a delivery not yet priced reads so, never £0 (doc 104 §5.6). */
const count = (n: number) => n.toLocaleString("en-GB");

/** How a line is fulfilled: staff only, never on the customer's page. */
const ROUTE_LABELS: Record<string, string> = {
  buy: "Buy",
  make: "Make",
  from_stock: "From stock",
};

// ── The pill ──────────────────────────────────────────────────────────────

/** The backend's label; green only for an accepted quote, open is grey, the rest neutral. */
export function QuoteStatusPill({
  status,
  label,
}: {
  status: QuoteStatus;
  label: string;
}) {
  return (
    <Badge
      variant={
        status === "accepted"
          ? "success"
          : status === "published"
            ? "secondary"
            : "neutral"
      }
    >
      {label}
    </Badge>
  );
}

// ── Details ───────────────────────────────────────────────────────────────

/** Under the facts when Details is open: who it went to and how it was sent. */
export function QuoteDetailsPanel({ quote }: { quote: QuoteView }) {
  const reminded = lastRemindedAt(quote);
  // A channel's draft: where its goods go, chosen on this page.
  const delivery = deliveryAddressOf(quote);
  return (
    <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {delivery && (
        <DetailItem label="Delivers to">
          {delivery.name}
          <span className="block text-muted-foreground">
            {delivery.lines.join(", ")}
          </span>
        </DetailItem>
      )}
      <DetailItem label="Contact">
        {quote.contact ? (
          <>
            {quote.contact.name}
            <span className="block text-muted-foreground">
              {quote.contact.email}
            </span>
          </>
        ) : (
          "No contact yet"
        )}
      </DetailItem>
      <DetailItem label="Made by">{quote.created_by}</DetailItem>
      {quote.published_by && (
        <DetailItem label="Published by">
          {quote.published_by.name}, {formatDateOrDash(quote.published_at)}
        </DetailItem>
      )}
      {quote.status !== "draft" && (
        <DetailItem label="Link last shared">
          {quote.last_shared_at
            ? formatDateOrDash(quote.last_shared_at)
            : "Not shared yet"}
        </DetailItem>
      )}
      <DetailItem label="Expires">
        {quote.expires_at
          ? formatDateOrDash(quote.expires_at)
          : "Set when it is published"}
      </DetailItem>
      <DetailItem label="Assigned rep">
        {quote.assigned_rep_name ?? "None"}
      </DetailItem>
      <DetailItem label="Reminders">
        {quote.reminder_interval_hours === null
          ? "Each working day"
          : `Every ${quote.reminder_interval_hours} hours, on working days`}
        {reminded && (
          <span className="block text-muted-foreground">
            Last {formatDateOrDash(reminded)}
          </span>
        )}
      </DetailItem>
      <DetailItem label="Copied to">
        {quote.cc_emails.length > 0 ? quote.cc_emails.join(", ") : "No one"}
      </DetailItem>
      {quote.required_on && (
        <DetailItem label="Needed by">
          {formatDateOrDash(quote.required_on)}
        </DetailItem>
      )}
      <DetailItem label="Who can accept">
        Whoever holds the link, for the whole quote
      </DetailItem>
      <DetailItem label="Note to the customer" wide>
        {quote.custom_note ? (
          <span className="whitespace-pre-line">{quote.custom_note}</span>
        ) : (
          <span className="text-muted-foreground">None</span>
        )}
      </DetailItem>
      <DetailItem label="Internal notes (staff only)" wide>
        {quote.internal_notes ? (
          <span className="whitespace-pre-line">{quote.internal_notes}</span>
        ) : (
          <span className="text-muted-foreground">None</span>
        )}
      </DetailItem>
    </dl>
  );
}

// ── The action box ────────────────────────────────────────────────────────

/** A closed quote's line, with the way on: its order, or the quote that replaced it. */
function ClosingLine({ quote }: { quote: QuoteView }) {
  const onward = quote.order_no
    ? {
        label: `Open ${quote.order_no}`,
        to: paths.orderDetail({ orderNo: quote.order_no }),
      }
    : quote.replaced_by
      ? {
          label: `Open ${quote.replaced_by.quote_no}`,
          to: paths.quoteDetail({ quoteNo: quote.replaced_by.quote_no }),
        }
      : null;
  return (
    <p>
      <b className="font-semibold">{quote.closing_label}</b>
      {onward && (
        <>
          {" "}
          <Link to={onward.to} className="underline underline-offset-2">
            {onward.label}
          </Link>
        </>
      )}
    </p>
  );
}

/** A channel's draft's checks: the rows that open the Draft step's lines, where they are decided. */
const DRAFT_CHECK_ROWS = new Set(["quantities", "alternatives"]);

export function QuoteActionBox({
  quote,
  isExpanded,
  onExpandedChange,
  onShowDraftLines,
}: {
  quote: QuoteView;
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  /** Open the Draft step, where a channel's draft's checks are decided. */
  onShowDraftLines?: () => void;
}) {
  const box = quoteActionBox(quote);
  const decision = paths.quoteDetail({
    quoteNo: quote.quote_no,
    step: "decision",
  });
  return (
    <ActionBoxLayout
      toDo={box.toDo.map((row) => ({
        key: row.key,
        tier: row.tier,
        verb: row.verb,
        thing: row.thing,
        reason: row.reason,
        // Nothing in Q1 re-sends a quote: staff tell the customer themselves.
        button:
          onShowDraftLines && DRAFT_CHECK_ROWS.has(row.key) ? (
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              aria-label={`Show the lines: ${row.thing}`}
              onClick={onShowDraftLines}
            >
              Show the lines
            </Button>
          ) : null,
      }))}
      waiting={box.waiting.map((row) => ({
        key: row.key,
        party: row.party,
        verb: row.verb,
        thing: (
          <Link
            to={decision}
            className="text-foreground underline decoration-muted-foreground/45 underline-offset-[3px] hover:decoration-current"
          >
            {row.thing}
          </Link>
        ),
        when: row.when,
        isAuto: false,
      }))}
      next={box.next}
      closed={quote.closing_label ? <ClosingLine quote={quote} /> : undefined}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
    />
  );
}

// ── Published: the lines ───────────────────────────────────────────────────────

/** The chosen option of an accepted line, if it had options. */
function chosenOption(line: QuoteLineView) {
  return line.quantity_options.find((o) => o.is_chosen) ?? null;
}

/** The line's quantity and price as the quote stands: one, its options, or the customer's pick. */
function LineQuantity({ line }: { line: QuoteLineView }) {
  if (line.is_included === false) return <>Left out by the customer</>;
  const chosen = chosenOption(line);
  const options = line.quantity_options;
  if (chosen)
    return (
      <>
        Chose {count(chosen.quantity)} · {optionPounds(chosen.price_pence)}
        <span className="text-muted-foreground">
          {" "}
          · of {options.map((o) => count(o.quantity)).join(" / ")}
        </span>
      </>
    );
  if (options.length > 0)
    return (
      <>
        {options
          .map((o) => `${count(o.quantity)} at ${optionPounds(o.price_pence)}`)
          .join(" · ")}
        <span className="text-muted-foreground"> · the customer picks one</span>
      </>
    );
  return <>{formatPriceOrNotYetPriced(line.price_pence)}</>;
}

/** Each quantity option, with its own split across the versions when there are two or more. */
function OptionsTable({ line }: { line: QuoteLineView }) {
  const isSplit = line.versions.length >= 2;
  // A quote made before delivery was priced per quantity has one delivery for all its quantities: no column.
  const hasDelivery = line.quantity_options.some(
    (o) => o.delivery_label != null,
  );
  return (
    <table className="w-full max-w-xl text-sm">
      <thead>
        <tr className="text-left text-xs text-muted-foreground">
          <th className="py-1 pr-4 font-medium">Quantity</th>
          {isSplit &&
            line.versions.map((v) => (
              <th key={v.position} className="py-1 pr-4 text-right font-medium">
                {versionWords(v)}
              </th>
            ))}
          <th className="py-1 pr-4 text-right font-medium">Lead time</th>
          {hasDelivery && (
            <th className="py-1 pr-4 text-right font-medium">Delivery</th>
          )}
          <th className="py-1 text-right font-medium">Price</th>
        </tr>
      </thead>
      <tbody>
        {line.quantity_options.map((o) => (
          <tr key={o.position}>
            <td className="py-1 pr-4 tabular-nums">
              {count(o.quantity)}
              {o.is_chosen && " (chosen)"}
            </td>
            {isSplit &&
              line.versions.map((v, i) => (
                <td
                  key={v.position}
                  className="py-1 pr-4 text-right tabular-nums"
                >
                  {count(o.version_quantities[i] ?? 0)}
                </td>
              ))}
            <td className="py-1 pr-4 text-right tabular-nums">
              {workingDaysLabel(o.working_days_to_arrival)}
            </td>
            {hasDelivery && (
              <td className="py-1 pr-4 text-right tabular-nums">
                {o.delivery_label}
              </td>
            )}
            <td className="py-1 text-right tabular-nums">
              {optionPounds(o.price_pence)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LineFacts({ line }: { line: QuoteLineView }) {
  return (
    <dl className="grid grid-cols-2 gap-3">
      <DetailItem label="Line" wide>
        {line.name_full}
      </DetailItem>
      <DetailItem
        label={
          line.quantity_options.length > 0
            ? "First quantity option"
            : "Quantity"
        }
      >
        {count(line.quantity)}
      </DetailItem>
      <DetailItem label="Price, ex VAT">
        {formatPriceOrNotYetPriced(line.price_pence)}
      </DetailItem>
      {line.is_optional && (
        <DetailItem label="Optional extra" wide>
          {line.is_included === null
            ? "Unticked on the customer's page until they tick it; not in the quote's total"
            : line.is_included
              ? "Ticked by the customer: on the order"
              : "Left out by the customer: not on the order"}
        </DetailItem>
      )}
      {line.versions.length > 0 && (
        <DetailItem label="Versions" wide>
          {line.versions
            .map((v) => `${versionWords(v)} ${count(v.quantity)}`)
            .join(" · ")}
        </DetailItem>
      )}
      {line.quantity_options.length > 0 && (
        <DetailItem label="Quantity options" wide>
          <OptionsTable line={line} />
        </DetailItem>
      )}
      {line.route && (
        <DetailItem label="Route (staff only)">
          {ROUTE_LABELS[line.route] ?? line.route}
        </DetailItem>
      )}
      {line.rule_sentence && (
        <DetailItem label="Arrival" wide>
          {line.rule_sentence}
        </DetailItem>
      )}
      {line.approve_by_sentence && (
        <DetailItem label="Approve by" wide>
          {line.approve_by_sentence}
        </DetailItem>
      )}
      {line.price_held_from && (
        <DetailItem label="Price" wide>
          {line.price_held_from.label}
        </DetailItem>
      )}
    </dl>
  );
}

export function QuoteLinesPanel({
  quote,
  focusLineNo,
  busy,
  onNameVersions,
  onChooseQuantities,
  onResolveAlternative,
  artworkHandlers = null,
}: {
  quote: QuoteView;
  focusLineNo: string | null;
  busy: boolean;
  /** Name a line's versions: offered on a line while the view carries its URL (quotes' Name versions). */
  onNameVersions?: (lineId: string) => void;
  /** A channel's draft: choose a line's quantities, offered while the view carries its URL. */
  onChooseQuantities?: (lineId: string) => void;
  /** A channel's draft: decide a possible alternative, offered while the view carries its URL. */
  onResolveAlternative?: (lineId: string) => void;
  /** The file column's steps (doc 66 §3); without them the files only read. */
  artworkHandlers?: QuoteArtworkHandlers | null;
}) {
  const focusRef = useFocusRef();
  const artwork = quote.artwork;
  return (
    <div className="space-y-2" data-section-id="quote.lines">
      {isChannelDraft(quote) && <DraftChecksSummary quote={quote} />}
      <PanelHeading
        title={quote.status === "draft" ? "Draft" : "Published"}
        info={
          quote.status === "draft"
            ? "A private draft: only staff can see it, and it has no link, expiry or reminders. Edit it as often as you like (it keeps this number); its prices are fixed when it is published."
            : "Priced and frozen at publishing: its lines, quantity options, optional extras and versions can't change. A change is a new quote that replaces this one. Version labels and names can change while it is published."
        }
        infoLabel="About the quote's lines"
        summary={`${quote.published_at ? `${short(quote.published_at)} to ${contactName(quote)}` : `for ${contactName(quote)}`} · ${pounds(quote.total_pence)}, ex VAT${quote.has_alternatives && quote.status !== "accepted" ? ", at the first quantities, without the extras" : ""}`}
      />
      <RailRows ariaLabel="Lines">
        {quote.lines.map((line) => {
          const no = quoteLineNo(quote, line);
          const isFocused = no === focusLineNo;
          const isLeftOut = line.is_included === false;
          const artworkLine =
            artwork?.lines.find((a) => a.line_ref === line.line_ref) ?? null;
          const isToCheck =
            (line.quantity_candidates ?? []).length > 0 ||
            line.is_possible_alternative;
          return (
            <Fragment key={line.id}>
              <RailRow
                rowRef={isFocused ? focusRef : undefined}
                isFocused={isFocused}
                state={isToCheck && quote.status === "draft" ? "to_do" : "done"}
                problem={null}
                label={no}
                name={
                  <>
                    <span className={isLeftOut ? "line-through" : undefined}>
                      {line.name_full}
                    </span>
                    {line.is_optional && (
                      <Badge variant="outline" className="ml-2 font-normal">
                        Optional extra
                      </Badge>
                    )}
                  </>
                }
                spokenName={
                  line.is_optional
                    ? `${line.name_full}, an optional extra`
                    : undefined
                }
                status={
                  <span className="tabular-nums">
                    <LineQuantity line={line} />
                    {line.versions.length >= 2 && (
                      <span className="text-muted-foreground">
                        {" "}
                        · {line.versions.length} versions:{" "}
                        {line.versions.map(versionWords).join(", ")}
                      </span>
                    )}
                    {line.delivers_to && (
                      <span className="text-muted-foreground">
                        {" "}
                        ·{" "}
                        {line.kind === "service"
                          ? `to ${line.delivers_to}`
                          : line.delivers_to}
                      </span>
                    )}
                    {line.is_optional && line.is_included === null && (
                      <span className="text-muted-foreground">
                        {" "}
                        · the customer ticks it to include it
                      </span>
                    )}
                    {line.price_held_from && (
                      <span className="text-muted-foreground">
                        {" "}
                        · {line.price_held_from.label}
                      </span>
                    )}
                  </span>
                }
                actions={
                  <>
                    {!!onNameVersions && !!line.set_version_labels_url && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        aria-label={`Name versions of ${no} ${line.name_full}`}
                        onClick={() => onNameVersions(line.id)}
                      >
                        Name versions…
                      </Button>
                    )}
                    {!!onChooseQuantities && !!line.choose_quantities_url && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        aria-label={`Choose quantities of ${no} ${line.name_full}`}
                        onClick={() => onChooseQuantities(line.id)}
                      >
                        Choose quantities…
                      </Button>
                    )}
                    {!!onResolveAlternative &&
                      !!line.resolve_alternative_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          aria-label={`Decide on ${no} ${line.name_full}, a possible alternative`}
                          onClick={() => onResolveAlternative(line.id)}
                        >
                          Decide…
                        </Button>
                      )}
                  </>
                }
                detail={
                  <ViewDetail title={`Line ${no} ${line.name_full}`}>
                    <LineFacts line={line} />
                  </ViewDetail>
                }
                expanded={
                  quote.status === "draft" &&
                  (isToCheck || thinPriceOf(line)) ? (
                    <DraftLineChecks quote={quote} line={line} />
                  ) : undefined
                }
              />
              {artworkLine && !isLeftOut && (
                <QuoteArtworkRows
                  line={artworkLine}
                  handlers={artworkHandlers}
                />
              )}
            </Fragment>
          );
        })}
      </RailRows>
    </div>
  );
}

// ── Customer decision ─────────────────────────────────────────────────────

/** What the customer chooses as they accept, or what they chose. */
function choicesWords(quote: QuoteView): string {
  const isAccepted = quote.status === "accepted";
  return quote.lines
    .flatMap((line) => {
      const no = quoteLineNo(quote, line);
      if (isAccepted) {
        if (line.is_included === false) return [`${no} left out`];
        const chosen = chosenOption(line);
        if (chosen) return [`${no} ${count(chosen.quantity)}`];
        return line.is_optional ? [`${no} included`] : [];
      }
      if (line.quantity_options.length > 0)
        return [
          `${no} one of ${line.quantity_options.map((o) => count(o.quantity)).join(" / ")}`,
        ];
      return line.is_optional ? [`${no} if ticked`] : [];
    })
    .join(" · ");
}

export function QuoteDecisionPanel({ quote }: { quote: QuoteView }) {
  const reminded = lastRemindedAt(quote);
  const isOpen = quote.status === "published";
  return (
    <div className="space-y-2" data-section-id="quote.decision">
      <PanelHeading
        title="Customer decision"
        info="Whoever holds the quote's link decides, for the whole quote. Reminders go while they decide, up to the day it expires."
        infoLabel="About the customer's decision"
      />
      <RailRows ariaLabel="The decision">
        {quote.status === "draft" ? (
          <RailRow
            state="to_do"
            problem={null}
            label={quote.quote_no}
            name="Not yet with the customer"
            status="a draft: only staff can see it until it is published and shared"
          />
        ) : isOpen && !quote.last_shared_at ? (
          <RailRow
            state="to_do"
            problem={null}
            label={quote.quote_no}
            name={`Not yet shared with ${contactName(quote)}`}
            status={`Published ${short(quote.published_at)} · expires ${short(quote.expires_at)} · share the link so they can decide`}
          />
        ) : isOpen ? (
          <RailRow
            state="in_progress"
            problem={quote.is_email_failed ? "at_risk" : null}
            label={quote.quote_no}
            name={`With ${contactName(quote)}`}
            status={[
              `Published ${short(quote.published_at)}`,
              quote.is_email_failed
                ? "the quote email could not be sent"
                : reminded && `reminded ${short(reminded)}`,
              `expires ${short(quote.expires_at)}`,
            ]
              .filter(Boolean)
              .join(" · ")}
          />
        ) : (
          <RailRow
            state="done"
            problem={null}
            label={quote.quote_no}
            name={quote.closing_label ?? quote.status_label}
            status={
              acceptedOnBehalfWords(quote) ??
              `Published ${short(quote.published_at)} to ${contactName(quote)}`
            }
            actions={
              quote.replaced_by && (
                <Button asChild size="sm" variant="outline">
                  <Link
                    to={paths.quoteDetail({
                      quoteNo: quote.replaced_by.quote_no,
                    })}
                  >
                    Open {quote.replaced_by.quote_no}
                  </Link>
                </Button>
              )
            }
          />
        )}
        {quote.has_alternatives && (isOpen || quote.status === "accepted") && (
          <RailRow
            state={isOpen ? "to_do" : "done"}
            problem={null}
            label="Choices"
            name={
              isOpen
                ? `${contactName(quote)} chooses as they accept`
                : "What they chose"
            }
            status={
              isOpen
                ? `${choicesWords(quote)} · nothing preselected on their page`
                : choicesWords(quote)
            }
          />
        )}
      </RailRows>
    </div>
  );
}

// ── Order ─────────────────────────────────────────────────────────────────

export function QuoteOrderPanel({ quote }: { quote: QuoteView }) {
  let body: ReactNode;
  if (quote.order_no)
    body = (
      <p className="text-sm">
        Acceptance made{" "}
        <ObjectChip
          label={quote.order_no}
          to={paths.orderDetail({ orderNo: quote.order_no })}
        />
        . The quote is frozen as it was accepted; everything live is on the
        order.
      </p>
    );
  else if (quote.status === "published" || quote.status === "draft")
    body = (
      <p className="text-sm text-muted-foreground">
        Becomes an order when {contactName(quote)} accepts it
        {quote.status === "draft" ? ", once it is published and shared" : ""}.
      </p>
    );
  else
    body = (
      <p className="text-sm text-muted-foreground">
        No order: the quote is closed.
      </p>
    );
  return (
    <div className="space-y-2" data-section-id="quote.order">
      <PanelHeading title="Order" />
      {body}
    </div>
  );
}

// ── Activity ──────────────────────────────────────────────────────────────

/** The quote's timeline, newest first, in the view's own sentences. */
export function QuoteTimeline({ entries }: { entries: QuoteTimelineEntry[] }) {
  if (entries.length === 0)
    return (
      <p className="text-sm text-muted-foreground">Nothing recorded yet.</p>
    );
  return (
    <ol className="space-y-1.5 text-sm">
      {entries.map((e, i) => (
        <li key={`${e.occurred_at}-${i}`}>
          <span className="tabular-nums text-muted-foreground">
            {formatDateTimeOrDash(e.occurred_at)}
          </span>
          <span className="text-muted-foreground"> · </span>
          {e.label}
        </li>
      ))}
    </ol>
  );
}
